import React, { useState } from 'react';

const EventSchedule = ({ 
  events, 
  selectedDate, 
  setSelectedDate, 
  getEventsForDate, 
  handleViewClick, 
  handleEditClick,
  setActiveTab 
}) => {
  const [calendarView, setCalendarView] = useState('month'); // 'month' or 'week'

  const handlePrevious = () => {
    if (calendarView === 'month') {
      setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1, 1));
    } else {
      const newDate = new Date(selectedDate);
      newDate.setDate(selectedDate.getDate() - 7);
      setSelectedDate(newDate);
    }
  };

  const handleNext = () => {
    if (calendarView === 'month') {
      setSelectedDate(new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 1));
    } else {
      const newDate = new Date(selectedDate);
      newDate.setDate(selectedDate.getDate() + 7);
      setSelectedDate(newDate);
    }
  };

  const handleToday = () => {
    setSelectedDate(new Date());
  };

  const renderCalendarDays = () => {
    const days = [];
    
    if (calendarView === 'month') {
      const daysInMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0).getDate();
      const firstDayOfMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1).getDay();
      
      // Previous month's trailing days
      const prevMonthLastDay = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 0).getDate();
      for (let i = firstDayOfMonth - 1; i >= 0; i--) {
        const day = prevMonthLastDay - i;
        days.push({ day, currentMonth: false, date: new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1, day) });
      }

      // Current month's days
      for (let d = 1; d <= daysInMonth; d++) {
        days.push({ day: d, currentMonth: true, date: new Date(selectedDate.getFullYear(), selectedDate.getMonth(), d) });
      }

      // Next month's leading days - ONLY fill until the end of the current week
      // This avoids showing an entire 6th row if not needed
      const remainingSlots = days.length % 7 === 0 ? 0 : 7 - (days.length % 7);
      for (let i = 1; i <= remainingSlots; i++) {
        days.push({ day: i, currentMonth: false, date: new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, i) });
      }
      
      // If we want to strictly enforce a clean 5-row look when possible, 
      // we stop here. If the month requires 6 rows (e.g., starts on Sat and has 31 days), 
      // the logic above will naturally include them.
    } else {
      // Week View logic
      const curr = new Date(selectedDate);
      const first = curr.getDate() - curr.getDay(); // First day is Sunday
      for (let i = 0; i < 7; i++) {
        const next = new Date(curr.getFullYear(), curr.getMonth(), first + i);
        days.push({ day: next.getDate(), currentMonth: next.getMonth() === selectedDate.getMonth(), date: next });
      }
    }

    return days.map((dayObj, index) => {
      const isToday = new Date().toDateString() === dayObj.date.toDateString();
      const isSelected = selectedDate.toDateString() === dayObj.date.toDateString();
      const dayEvents = getEventsForDate(dayObj.date);

      return (
        <div
          key={index}
          className={`premium-calendar-day ${!dayObj.currentMonth ? 'not-current-month' : ''} ${isToday ? 'is-today' : ''} ${isSelected ? 'is-selected' : ''} ${dayEvents.length > 0 ? 'has-events' : ''}`}
          onClick={() => setSelectedDate(dayObj.date)}
        >
          <div className="day-label-wrapper">
            <span className="day-label">{dayObj.day}</span>
          </div>
          
          <div className="apt-preview-container">
            {dayEvents.slice(0, calendarView === 'month' ? 2 : 5).map((event, idx) => (
              <div 
                key={event._id || event.id} 
                className={`apt-preview status-${event.status?.toLowerCase() || 'published'}`}
                title={event.title}
                onClick={(e) => {
                    e.stopPropagation();
                    handleViewClick(event);
                }}
              >
                {event.title}
              </div>
            ))}
            {dayEvents.length > (calendarView === 'month' ? 2 : 5) && (
              <div className="text-muted extra-small ps-1">
                +{dayEvents.length - (calendarView === 'month' ? 2 : 5)} more
              </div>
            )}
          </div>
        </div>
      );
    });
  };

  const selectedDateEvents = getEventsForDate(selectedDate);

  return (
    <div className="schedule-section mt-4">
      <div className="row g-4">
        {/* Left Side: Calendar */}
        <div className="col-lg-8">
          <div className="premium-dashboard-card">
            {/* Calendar Header */}
            <div className="premium-card-header">
              <div className="d-flex align-items-center gap-4">
                <h5 className="fw-bold mb-0 text-dark">
                  {selectedDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
                </h5>
                <div className="view-toggle-group">
                  <button
                    onClick={() => setCalendarView('month')}
                    className={`view-toggle-btn ${calendarView === 'month' ? 'active' : ''}`}
                  >
                    Month
                  </button>
                  <button
                    onClick={() => setCalendarView('week')}
                    className={`view-toggle-btn ${calendarView === 'week' ? 'active' : ''}`}
                  >
                    Week
                  </button>
                </div>
              </div>

              <div className="d-flex align-items-center gap-2">
                <div className="d-flex gap-1">
                  <button
                    onClick={handlePrevious}
                    className="btn btn-light btn-sm d-flex align-items-center gap-1 px-2"
                    style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', height: '32px' }}
                    title="Previous"
                  >
                    <i className="isax isax-arrow-left-2" style={{ fontSize: '16px' }}></i>
                    <span style={{ fontSize: '11px', fontWeight: '600' }}>Prev</span>
                  </button>
                  <button
                    onClick={handleNext}
                    className="btn btn-light btn-sm d-flex align-items-center gap-1 px-2"
                    style={{ background: '#f1f5f9', border: '1px solid #e2e8f0', height: '32px' }}
                    title="Next"
                  >
                    <span style={{ fontSize: '11px', fontWeight: '600' }}>Next</span>
                    <i className="isax isax-arrow-right-3" style={{ fontSize: '16px' }}></i>
                  </button>
                </div>
              </div>
            </div>

            {/* Weekday Headers */}
            <div className="premium-calendar-grid py-2 border-bottom">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="premium-weekday">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid */}
            <div className="premium-calendar-grid">
              {renderCalendarDays()}
            </div>
          </div>
        </div>

        {/* Right Side: Event Details */}
        <div className="col-lg-4">
          <div className="premium-dashboard-card h-100">
            <div className="premium-card-header">
              <div>
                <h6 className="fw-bold mb-0 text-dark">
                  {selectedDate.toLocaleDateString('default', { weekday: 'long', month: 'short', day: 'numeric' })}
                </h6>
                <p className="text-muted extra-small mb-0 mt-1">
                  {selectedDateEvents.length} Event{selectedDateEvents.length !== 1 ? 's' : ''} scheduled
                </p>
              </div>
            </div>

            <div className="p-4 overflow-auto" style={{ maxHeight: '600px' }}>
              {selectedDateEvents.length > 0 ? (
                selectedDateEvents.map((event) => (
                  <div key={event._id || event.id} className={`premium-event-card status-${event.status?.toLowerCase() || 'published'}`}>
                    <div className="event-time-row">
                      <div className="time-badge">
                        <i className="isax isax-clock me-2 text-primary"></i>
                        {new Date(event.startDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                      <span className={`status-badge-premium status-${event.status?.toLowerCase() || 'published'}-bg`}>
                        {event.status || 'Published'}
                      </span>
                    </div>

                    <h6 className="fw-bold text-dark mb-2">{event.title}</h6>
                    
                    <div className="d-flex align-items-center text-muted extra-small mb-1">
                      <i className="isax isax-location me-2 text-danger"></i>
                      <span>{event.venue || event.locationType || 'TBA'}</span>
                    </div>
                    
                    <div className="d-flex align-items-center text-muted extra-small mb-3">
                      <i className="isax isax-ticket me-2 text-primary"></i>
                      <span>{event.ticketTiers?.length || 0} Ticket Tiers</span>
                    </div>

                    <div className="event-action-group">
                      <button 
                        className="action-btn-pill btn-view"
                        onClick={() => handleViewClick(event)}
                      >
                        <i className="isax isax-eye"></i> View
                      </button>
                      <button 
                        className="action-btn-pill btn-edit"
                        onClick={() => handleEditClick(event)}
                      >
                        <i className="isax isax-edit"></i> Edit
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="empty-state-container">
                  <div className="empty-state-icon">
                    <i className="isax isax-calendar-remove fs-1"></i>
                  </div>
                  <h6 className="fw-bold text-dark">No events found</h6>
                  <p className="text-muted extra-small px-3">
                    There are no events scheduled for this day.
                  </p>
                  <button 
                    className="btn btn-sm btn-outline-primary rounded-pill px-4 mt-2"
                    onClick={() => setActiveTab('create')}
                  >
                    Create Event
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventSchedule;
